// @ts-check

const DEFAULT_STOREFRONT = process.env.GATSBY_SHOP_STOREFRONT_NAME || "shop"

const DEFAULT_MAX_QUANTITY = process.env.GATSBY_DEFAULT_ITEM_MAX_QUANTITY
  ? Number.parseInt(process.env.GATSBY_DEFAULT_ITEM_MAX_QUANTITY, 10)
  : 10

const STORYBLOK_ITEM_PATHS = [
  "settings/products/",
  "settings/bundles/",
  "settings/online-products/",
]

/**
 * @param {string} url
 * @returns {string}
 */
const storyblokUrlToInternalUrl = (url) => {
  return url?.replace(process.env.GATSBY_STORYBLOK_ROOT || "", "") ?? ""
}

/**
 * @param {Storyblok.Link} link
 * @returns {string}
 */
const getUrlFromStoryblokLink = (link) => {
  if (link.linktype === "email") {
    return `mailto:${link.email}`
  }

  const urlReference = link.url || link.cached_url
  if (
    urlReference.includes("http") ||
    urlReference.includes("tel:") ||
    urlReference.startsWith("#")
  ) {
    return urlReference
  }
  return (
    // TODO: Check if prefix path still needed with V2 implementation
    `${process.env.GATSBY_PREFIX_PATH || ""}/${storyblokUrlToInternalUrl(
      urlReference
    )}${urlReference && urlReference.slice(-1) !== "/" ? "/" : ""}` || "/"
  )
}

/**
 * @param {Storyblok.Link | undefined} link
 * @returns {boolean}
 */
const linkIsNotNull = (link) => {
  return !!(link && (link.cached_url || link.url || link.email))
}

/**
 * @param {Storyblok.Link | undefined} link
 * @returns {YocoCom.YocoLink | undefined}
 */
const storyblokLinkToYocoLink = (link) => {
  if (!linkIsNotNull(link) || !link) {
    return undefined
  }

  return {
    url: getUrlFromStoryblokLink(link),
    target: link.target || "_self",
    title: link.title,
    rel: link.rel,
    queryParams: link.queryParams,
  }
}

/**
 * @typedef {{ price: string, originalPrice?: string }} ProductPrice
 */

/**
 * @param {YocoCom.SdkProduct} product
 * @returns {ProductPrice}
 */
const getProductPrices = (product) => {
  /** @type {ProductPrice} */
  const sdkPrices = { price: product.unit_price }

  if (Number.parseFloat(product.original_unit_price) > 0) {
    sdkPrices.originalPrice = product.original_unit_price
  }

  return sdkPrices
}

/**
 * @param {YocoCom.SdkBundle} bundle
 * @returns {ProductPrice}
 */
const getBundlePrices = (bundle) => {
  /** @type {ProductPrice} */
  const sdkPrices = { price: bundle.unit_price }

  if (Number.parseFloat(bundle.original_unit_price) > 0) {
    sdkPrices.originalPrice = bundle.original_unit_price
  }

  return sdkPrices
}

/**
 * @param {string} price
 * @param {string} [originalPrice]
 * @returns {boolean}
 */
const hasDiscountedPrice = (price, originalPrice) => {
  if (!originalPrice) return false

  const original = Number.parseFloat(originalPrice)
  const current = Number.parseFloat(price)

  return original > 0 && original - current > 0
}

/**
 * @param {string} [price]
 * @returns {number | undefined}
 */
const parseConsolidatedPrice = (price) => {
  if (!price) {
    return undefined
  }

  const parsedPrice = Number.parseFloat(price)

  return Number.isNaN(parsedPrice) ? undefined : parsedPrice
}

/**
 * @param {string} storyblokUuid
 * @returns {string}
 */
const getConsolidatedItemNodeKey = (storyblokUuid) =>
  `ConsolidatedItem-${storyblokUuid}`

/**
 * @param {YocoCom.SdkProduct} product
 * @param {string} sku
 * @param {string} [storefront]
 * @returns {boolean}
 */
const isSdkProductMatch = (product, sku, storefront = DEFAULT_STOREFRONT) =>
  product.sku === sku &&
  Boolean(product.is_listed_in_catalogue) &&
  product.storefronts.includes(storefront)

/**
 * @param {YocoCom.SdkBundle} bundle
 * @param {string} bundleCode
 * @param {string} [storefront]
 * @returns {boolean}
 */
const isSdkBundleMatch = (
  bundle,
  bundleCode,
  storefront = DEFAULT_STOREFRONT
) =>
  bundle.bundle_code === bundleCode &&
  Boolean(bundle.is_listed_in_catalogue) &&
  bundle.storefronts.includes(storefront)

/**
 * @template {{ sdkIndex?: number }} TRecord
 * @param {TRecord[]} candidates
 * @returns {TRecord | undefined}
 */
const selectEarliestSdkRecord = (candidates) =>
  candidates.reduce(
    (earliest, candidate) =>
      !earliest ||
      (candidate.sdkIndex ?? Number.MAX_SAFE_INTEGER) <
        (earliest.sdkIndex ?? Number.MAX_SAFE_INTEGER)
        ? candidate
        : earliest,
    /** @type {TRecord | undefined} */ (undefined)
  )

/**
 * @param {YocoCom.SdkProduct[]} products
 * @param {string} sku
 * @param {string} [storefront]
 * @returns {YocoCom.SdkProduct | undefined}
 */
const selectSdkProduct = (products, sku, storefront = DEFAULT_STOREFRONT) =>
  selectEarliestSdkRecord(
    products.filter((product) => isSdkProductMatch(product, sku, storefront))
  )

/**
 * @param {YocoCom.SdkBundle[]} bundles
 * @param {string} bundleCode
 * @param {string} [storefront]
 * @returns {YocoCom.SdkBundle | undefined}
 */
const selectSdkBundle = (
  bundles,
  bundleCode,
  storefront = DEFAULT_STOREFRONT
) =>
  selectEarliestSdkRecord(
    bundles.filter((bundle) => isSdkBundleMatch(bundle, bundleCode, storefront))
  )

/**
 * @param {YocoCom.SdkProduct | YocoCom.SdkBundle} sdkItem
 * @param {string} storyblokName
 * @returns {string}
 */
const resolveItemName = (sdkItem, storyblokName) =>
  sdkItem.short_name || sdkItem.name || storyblokName

/**
 * @param {string | undefined} value
 * @returns {number | undefined}
 */
const parseOrderPriority = (value) => {
  if (value === undefined || value === null || value === "") return undefined

  const parsed = Number(value)

  return Number.isNaN(parsed) ? undefined : parsed
}

/**
 * @param {Storyblok.ProductConfig | Storyblok.BundleConfig | Storyblok.OnlineProductConfig} config
 */
const getSharedItemFields = (config) => ({
  thumbnail: config.thumbnailImage,
  descriptionHeader: config.descriptionHeader,
  imageButtonDescription: config.imageButtonDescription,
  galleryItems: config.galleryItems ?? [],
  filterTags: config.filterTags ?? [],
  isListed: config.is_listed === true,
  orderPriority: parseOrderPriority(config.orderPriority),
  showInHero: config.showInHero === true,
  heroOrderPriority: parseOrderPriority(config.heroOrderPriority),
  badge: config.badges?.[0],
  boxContent: config.boxContent?.map((boxItem) => ({
    title: boxItem.title,
    imageUrl: boxItem.image?.filename,
    imageAlt: boxItem.image?.alt || "",
  })),
  disclaimer: config.disclaimer
    ? { richText: config.disclaimer, color: config.disclaimerColor }
    : undefined,
  suitedFor: config.suitedFor,
  industries: config.industries,
  paymentStripDark: config.paymentStripDark,
  paymentStripLight: config.paymentStripLight,
})

/**
 * @param {Storyblok.ProductConfig | Storyblok.BundleConfig} config
 */
const getRecommendedProductFields = (config) => ({
  recommendedProducts: config.recommendedProducts,
  showRecommendedProducts: config.showRecommendedProducts,
  recommendedProductTitle: config.recommendedProductTitle,
  recommendedProductDescription: config.recommendedProductDescription,
  recommendedProductDescriptionCartOverview:
    config.recommendedProductDescriptionCartOverview,
})

/**
 * @param {Storyblok.ProductConfig} config
 * @param {YocoCom.SdkProduct} sdkProduct
 * @param {string} storyblokUuid
 * @returns {YocoCom.ConsolidatedProductItem}
 */
const consolidateProduct = (config, sdkProduct, storyblokUuid) => {
  const prices = getProductPrices(sdkProduct)
  const price = parseConsolidatedPrice(prices.price)
  const originalPrice = parseConsolidatedPrice(prices.originalPrice)
  const name = resolveItemName(sdkProduct, config.name)

  return {
    ...getSharedItemFields(config),
    ...getRecommendedProductFields(config),
    storefront: config.storefront ?? DEFAULT_STOREFRONT,
    quantity: 1,
    type: "product",
    storyblokUuid,
    sku: config.sku ?? sdkProduct.sku,
    name,
    catalogueTitle: config.catalogueTitleOverride || config.name,
    cartTitle: config.overrideCartTitle || name,
    price: prices.price,
    originalPrice: prices.originalPrice,
    savingsAmount: originalPrice && price ? originalPrice - price : 0,
    onPromotion: hasDiscountedPrice(prices.price, prices.originalPrice),
    priceSideScript: config.priceSuffixLabel,
    priceSubScript: config.priceInfoText,
    maxQuantity: sdkProduct.max_quantity_per_cart ?? DEFAULT_MAX_QUANTITY,
    reasonNotAvailable: sdkProduct.reason_not_available,
    reviewSkus: [config.sku],
    shortDescription: sdkProduct.short_description || sdkProduct.description,
    description: sdkProduct.description || sdkProduct.short_description,
    ctaText: config.buyNowText || "Buy Now",
    ctaLink: storyblokLinkToYocoLink(config.buyLink),
    detailsLink: storyblokLinkToYocoLink(config.detailsLink),
    waitlistLink: storyblokLinkToYocoLink(config.waitlistLink),
    thirdPartyReviews: sdkProduct.third_party_reviews,
  }
}

/**
 * @param {Storyblok.BundleConfig} config
 * @param {YocoCom.SdkBundle} sdkBundle
 * @param {string} storyblokUuid
 * @returns {YocoCom.ConsolidatedBundleItem}
 */
const consolidateBundle = (config, sdkBundle, storyblokUuid) => {
  const prices = getBundlePrices(sdkBundle)
  const price = parseConsolidatedPrice(prices.price)
  const originalPrice = parseConsolidatedPrice(prices.originalPrice)
  const name = resolveItemName(sdkBundle, config.name)

  return {
    ...getSharedItemFields(config),
    ...getRecommendedProductFields(config),
    storefront: config.storefront ?? DEFAULT_STOREFRONT,
    quantity: 1,
    type: "bundle",
    storyblokUuid,
    bundleCode: config.code,
    name,
    catalogueTitle: config.catalogueTitleOverride || config.name,
    cartTitle: config.overrideCartTitle || name,
    price: prices.price,
    originalPrice: prices.originalPrice,
    savingsAmount: originalPrice && price ? originalPrice - price : 0,
    onPromotion: hasDiscountedPrice(prices.price, prices.originalPrice),
    priceSideScript: config.priceSidescript,
    priceSubScript: config.priceSubscript,
    maxQuantity: sdkBundle.max_quantity_per_cart ?? DEFAULT_MAX_QUANTITY,
    reasonNotAvailable: sdkBundle.reason_not_available,
    reviewSkus: sdkBundle.content_skus ?? [],
    shortDescription: sdkBundle.short_description || sdkBundle.description,
    description: sdkBundle.description || sdkBundle.short_description,
    ctaText: config.buyNowText || "Buy Now",
    ctaLink: storyblokLinkToYocoLink(config.shopPage),
    detailsLink: storyblokLinkToYocoLink(config.cataloguePage),
    waitlistLink: storyblokLinkToYocoLink(config.waitlistLink),
  }
}

/**
 * @param {Storyblok.OnlineProductConfig} config
 * @param {string} storyblokUuid
 * @returns {YocoCom.ConsolidatedOnlineProductItem}
 */
const consolidateOnlineProduct = (config, storyblokUuid) => ({
  ...getSharedItemFields(config),
  type: "onlineProduct",
  storyblokUuid,
  name: config.title,
  catalogueTitle: config.title,
  shortDescription: config.description,
  ctaText: config.ctaText || "Buy Now",
  ctaLink: storyblokLinkToYocoLink(config.ctaLink),
  priceLabelOverride: config.priceLabel
    ? { label: config.priceLabel, caption: config.priceLabelCaption }
    : undefined,
})

module.exports = {
  DEFAULT_STOREFRONT,
  DEFAULT_MAX_QUANTITY,
  STORYBLOK_ITEM_PATHS,
  parseOrderPriority,
  storyblokUrlToInternalUrl,
  getUrlFromStoryblokLink,
  linkIsNotNull,
  storyblokLinkToYocoLink,
  getProductPrices,
  getBundlePrices,
  hasDiscountedPrice,
  parseConsolidatedPrice,
  getConsolidatedItemNodeKey,
  isSdkProductMatch,
  isSdkBundleMatch,
  selectSdkProduct,
  selectSdkBundle,
  consolidateProduct,
  consolidateBundle,
  consolidateOnlineProduct,
}
